import { clearJWTToken, getOrRefreshAccessToken, UnauthorizedError } from '@support/jwt';
import { ID, UserSession } from '@app/types';
import { ajax, AjaxResponse, AjaxTimeoutError } from 'rxjs/ajax';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { lastValueFrom, Observable, throwError } from 'rxjs';
import { EventSourceMessage, fetchEventSource } from '@microsoft/fetch-event-source';

// templated via Vite
// @ts-ignore
export const API_HOST: string = DEFINE_API_HOST;
// @ts-ignore
export const SEARCH_HOST: string = DEFINE_SEARCH_HOST;
// @ts-ignore
export const ACTIVITY_API_HOST: string = DEFINE_ACTIVITY_API_HOST;
// @ts-ignore
export const NEXUS_API_HOST: string = DEFINE_NEXUS_API_HOST;

const appendQueryParams = (url: string, query: { [key: string]: string }): string => {
  const queryKeys = Object.keys(query);

  if (queryKeys.length == 0) {
    return url;
  }

  const queryString = queryKeys
    .map((opt: string): string => `${opt}=${encodeURIComponent(query[opt])}`)
    .join('&');
  return `${url}?${queryString}`;
};
export type MethodType = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
export type ApiRequestOptions = {
  [key: string]: any;
  noAuthorization?: boolean;
  noContentType?: boolean;
  noTimeZone?: boolean;
  /**
   * Select which API version to use in requests
   * @default 'v1'
   */
  apiVersion?: 'v1' | 'v2';
  /**
   * Does not include the API version string in the path.
   * @default false
   */
  skipApiVersion?: boolean;
  /**
   * Appends timestamp to request (typically for cache-busting)
   */
  timeStamp?: boolean;
  /**
   * Assumes the provided URL is the full path to the request,
   * and no API path information will be prepended.
   * @default false
   */
  isFullPath?: boolean;
};
export const apiObservable = <T = any>(
  method: MethodType,
  path: string,
  content: any = {},
  options: ApiRequestOptions = {},
): Observable<AjaxResponse<T>> => {
  const { query = {}, ...body } = content;

  if (!options.noTimeZone) {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!!tz) {
      query.timezone = tz;
    }
  }

  if (options.timeStamp) {
    query.ts = `${Date.now()}`;
  }

  if (!options.isFullPath) {
    const versionString = options.skipApiVersion ? '' : `/${options.apiVersion ?? 'v1'}`;
    path = `${API_HOST}api${versionString}${path.startsWith('/') ? '' : '/'}${path}`;
  }

  const url = appendQueryParams(path, query);

  let headers = <Record<string, string>>{
    'Content-Type': 'application/json',
    ...(options.headers ?? {}),
  };

  // FormData needs no Content-Type header, because the browser will set it itself
  if (
    (options.noContentType && headers.hasOwnProperty('Content-Type')) ||
    content instanceof FormData
  ) {
    delete headers['Content-Type'];
  }

  if (options.noAuthorization) {
    return ajax<T>({
      url,
      method: method.toUpperCase(),
      headers,
      body: content instanceof FormData ? content : body,
    });
  }

  return getOrRefreshAccessToken().pipe(
    catchError((err) => {
      if (err instanceof UnauthorizedError) {
        window.location.href = '/login';
      }
      return throwError(() => err);
    }),
    mergeMap((accessToken) => {
      headers.authorization = `Bearer ${accessToken}`;

      return ajax<T>({
        url,
        method: method.toUpperCase(),
        headers,
        body: content instanceof FormData ? content : body,
      }).pipe(
        catchError((err) => {
          if (!options.noAuthorization && err.status === 401) {
            clearJWTToken();
            window.location.href = '/login';
          }
          return throwError(() => err);
        }),
      );
    }),
  );
};

export async function apiRequest<T = void>(
  method: MethodType,
  path: string,
  content: any = {},
  options: ApiRequestOptions = {},
): Promise<T> {
  const response = await lastValueFrom(apiObservable<T>(method, path, content, options));
  return response.response;
}

export async function apiFileRequest(
  path: string,
  options: ApiRequestOptions = {},
): Promise<Response> {
  const accessToken = await lastValueFrom(getOrRefreshAccessToken());
  const url = `${API_HOST}api/${options.apiVersion ?? 'v1'}${
    path.startsWith('/') ? '' : '/'
  }${path}`;

  return fetch(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...options.headers,
    },
  });
}

export async function apiRequestWithRetry<T = void>(
  method: MethodType,
  path: string,
  content: any = {},
  options: ApiRequestOptions = {},
): Promise<T> {
  let attempts = 5;
  while (attempts > 0) {
    --attempts;
    try {
      const response = await lastValueFrom(apiObservable<T>(method, path, content, options));
      return response.response;
    } catch (e) {
      if (e instanceof AjaxTimeoutError) {
        continue;
      }

      // rethrow everything else
      throw e;
    }
  }

  throw new Error('API request failed after 5 attempts');
}

interface SearchAPIHit<DocumentType> {
  document: DocumentType;
  text_match: number;
  highlights: {
    field: string;
    matched_tokens: string[];
    snippet: string;
  }[];
}

interface SearchAPIHits<T> {
  total: number;
  hits: (SearchAPIHit<T> | T)[];
}

interface SearchAPIResponse<T> {
  facet_counts: unknown[];
  found: number;
  // "transformed" version of the response replaces hits with the documents
  hits: SearchAPIHits<T>;
  page: number;
  request_params: Record<string, unknown>;
  search_cutoff: boolean;
  search_time_ms: number;
}

export const activityApiObservable = <T extends Record<string, unknown> = Record<string, unknown>>(
  method: string,
  path: string,
  options: any = {},
) => {
  const { query = {}, body } = options; // handle aborting here if no user?
  const url = appendQueryParams(`${ACTIVITY_API_HOST}${path}`, query);

  let headers = <Record<string, string>>{
    ...(method === 'GET'
      ? {}
      : {
          'Content-Type': 'application/json',
        }),
    ...(options.headers ?? {}),
  };

  return getOrRefreshAccessToken().pipe(
    catchError((err) => {
      if (err instanceof UnauthorizedError) {
        window.location.href = '/login';
      }
      return throwError(() => err);
    }),
    mergeMap((accessToken) => {
      headers.authorization = `Bearer ${accessToken}`;
      return ajax<SearchAPIResponse<T>>({
        url,
        method,
        headers,
        body,
      });
    }),
  );
};

export const searchApiObservable = <T extends Record<string, unknown> = Record<string, unknown>>(
  method: string,
  path: string,
  options: any = {},
) => {
  const { query = {}, body } = options; // handle aborting here if no user?
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (!!tz) {
    query.timezone = tz;
  }
  const url = appendQueryParams(`${SEARCH_HOST}${path}`, query);

  let headers = <Record<string, string>>{
    ...(options.headers ?? {}),
  };

  return getOrRefreshAccessToken().pipe(
    catchError((err) => {
      if (err instanceof UnauthorizedError) {
        window.location.href = '/login';
      }
      return throwError(() => err);
    }),
    mergeMap((accessToken) => {
      headers.authorization = `Bearer ${accessToken}`;

      return ajax<SearchAPIResponse<T>>({
        url,
        method: method.toUpperCase(),
        headers,
        body,
      }).pipe(
        map((payload) => {
          if (path !== 'search') {
            return payload;
          }

          payload.response.page = query.page || 1;
          payload.response.found = payload.response.hits?.total;
          return payload;
        }),
      );
    }),
  );
};

// FIXME is there such a thing as a method that isn't GET?
export async function searchApiRequest<T extends Record<string, unknown> = Record<string, unknown>>(
  method: MethodType,
  path: string,
  options: any = {},
): Promise<SearchAPIResponse<T>> {
  const response = await lastValueFrom(searchApiObservable<T>(method, path, options));
  return response.response;
}

type ConversationEventType =
  | 'conversation'
  | 'message'
  | 'contacts'
  | 'conversation_contact'
  | 'close'
  | 'error';

export interface ConversationEvent {
  type?: ConversationEventType;
  conversation_id?: ID;
  message_id?: ID;
  segment?: string;
  contacts?: ID[];
  contact_id?: ID;
}

export const conversationObservable = ({
  prompt,
  conversationID: ongoingConversationID,
  targetContactID,
  regenerate,
}: {
  prompt: string;
  conversationID?: ID;
  targetContactID?: ID;
  regenerate?: boolean;
}): Observable<ConversationEvent> => {
  return getOrRefreshAccessToken().pipe(
    catchError((err) => {
      if (err instanceof UnauthorizedError) {
        window.location.href = '/login';
      }
      return throwError(() => err);
    }),
    mergeMap((accessToken) => {
      return new Observable((subscriber) => {
        let conversation_id = ongoingConversationID;
        let message_id = undefined;

        const onmessage = (event: EventSourceMessage) => {
          let conversationEvent: ConversationEvent = {};

          try {
            if (!!event.data) {
              conversationEvent = JSON.parse(event.data);
            }
          } catch (_) {
            // ignore parsing error
          }

          if (conversationEvent.type === 'conversation' && !conversation_id) {
            conversation_id = conversationEvent.conversation_id;
          }

          if (!message_id && conversationEvent.message_id) {
            message_id = conversationEvent.message_id;
          }

          subscriber.next({ ...conversationEvent, conversation_id });
        };

        try {
          fetchEventSource(`${NEXUS_API_HOST}chat/`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json; charset=utf-8',
            },
            body: JSON.stringify({
              message: prompt,
              conversation_id: ongoingConversationID,
              contact_id: targetContactID,
              regenerate: !!regenerate,
            }),
            onmessage,
            onclose: () => {
              subscriber.next({ type: 'close', conversation_id, message_id });
              subscriber.complete();
            },
            onerror(err) {
              subscriber.next({ type: 'error', conversation_id, message_id });
              subscriber.complete();
              // throwing here interrupts the retry logic
              throw err; // Error('CHAT ERROR');
            },
          });
        } catch (error) {
          // we can stop logging if this gets too noisy, but for now it's helpful
          console.error('error in conversationObservable', error);
        }
      });
    }),
  );
};

export async function checkUser(): Promise<UserSession> {
  return apiRequest<UserSession>('GET', '/users/self/')
    .then(async (data: UserSession) => data)
    .catch((err) => {
      if (err.status === 401) {
        clearJWTToken();
        window.location.href = '/login';
      }
      throw err;
    });
}
